import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Text,
  Textarea,
  mergeClasses,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ANNOTATION_DATA_TYPE_INT } from '../../../../constants/index.js';
import type { DataEntry } from '../../../../services/dataHubService.interface.js';
import type { DataHubService } from '../../../../services/dataHubService.js';
import { withCorrelationId } from '../../../../services/usageContext.js';
import { useScenarioFactory } from '../../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../../telemetry/scenarios.js';
import { useStyles } from '../DatasetAnnotationsTab.style.js';

const MAX_DATA_ENTRIES_PAGE_SIZE = 100;

function truncateId(id: string, len = 14): string {
  return id.length > len ? `${id.slice(0, len)}…` : id;
}

// ── Raw data picker ────────────────────────────────────────────────────

interface RawDataPickerProps {
  entries: DataEntry[];
  selectedId: string;
  onSelect: (id: string) => void;
  isLoading: boolean;
}

function RawDataPicker({
  entries,
  selectedId,
  onSelect,
  isLoading,
}: RawDataPickerProps) {
  const styles = useStyles();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        (e.name?.toLowerCase().includes(q) ?? false) ||
        e.dataId.toLowerCase().includes(q),
    );
  }, [entries, search]);

  return (
    <>
      <Field label="Filter data entries">
        <Input
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          placeholder="Search by name or ID…"
        />
      </Field>
      <div className={styles.pickerContainer}>
        {isLoading ? (
          <div className={styles.pickerLoadingCenter}>
            <Spinner size="tiny" label="Loading data entries…" />
          </div>
        ) : filtered.length === 0 ? (
          <Text size={200} className={styles.pickerEmptyText}>
            No matching data entries.
          </Text>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr className={styles.tableHeader}>
                <th className={styles.tableHeaderCell}>Name</th>
                <th className={styles.tableHeaderCell}>ID</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr
                  key={e.dataId}
                  className={mergeClasses(
                    styles.tableRow,
                    selectedId === e.dataId
                      ? styles.pickerRowSelected
                      : styles.pickerRow,
                  )}
                  onClick={() => onSelect(e.dataId)}
                >
                  <td className={styles.tableCell}>
                    {e.name ?? truncateId(e.dataId, 12)}
                  </td>
                  <td className={styles.tableCell}>
                    <span className={styles.monoValue}>
                      {truncateId(e.dataId, 12)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ── Dialog ─────────────────────────────────────────────────────────────

export interface AddAnnotationDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dataSetId: string;
  dataSetVersion: number;
  annotationSetId: string;
  schemaName: string;
  service: DataHubService;
  onCreated: () => void;
}

export function AddAnnotationDataDialog({
  open,
  onOpenChange,
  dataSetId,
  dataSetVersion,
  annotationSetId,
  schemaName,
  service,
  onCreated,
}: AddAnnotationDataDialogProps) {
  const styles = useStyles();
  const scenarioFactory = useScenarioFactory();

  const [name, setName] = useState('');
  const [rawDataId, setRawDataId] = useState('');
  const [payloadRaw, setPayloadRaw] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [dataEntries, setDataEntries] = useState<DataEntry[]>([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoadingEntries(true);
    service
      .listDataEntries({
        dataSetId,
        dataSetVersion,
        pageSize: MAX_DATA_ENTRIES_PAGE_SIZE,
        includeCount: false,
      })
      .then((result) => {
        if (!cancelled) setDataEntries(result.items);
      })
      .catch(() => {
        /* ignore — picker will be empty */
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEntries(false);
      });
    return () => {
      cancelled = true;
    };
  }, [service, dataSetId, dataSetVersion, open]);

  const resetForm = useCallback(() => {
    setName('');
    setRawDataId('');
    setPayloadRaw('');
    setJsonError(null);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);
    setJsonError(null);

    let parsedPayload: Record<string, unknown> | undefined;
    if (payloadRaw.trim()) {
      try {
        parsedPayload = JSON.parse(payloadRaw) as Record<string, unknown>;
      } catch {
        setJsonError('Invalid JSON payload.');
        return;
      }
    }

    setIsSubmitting(true);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeCreateAnnotationData,
    );
    try {
      await service.createAnnotationData(
        dataSetId,
        annotationSetId,
        {
          dataType: ANNOTATION_DATA_TYPE_INT,
          name: name.trim(),
          rawDataId: rawDataId.trim(),
          category: schemaName,
          payload: parsedPayload,
        },
        // One created annotation = one tracked user action. Correlate it to
        // the annotation set; TEDH rolls usage up to the parent dataset via
        // the URL.
        {
          sourceId: annotationSetId,
          extras: withCorrelationId(scenario.queryId),
        },
      );
      scenario.complete('success');
      resetForm();
      onCreated();
      onOpenChange(false);
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setError(
        err instanceof Error ? err.message : 'Failed to create annotation data',
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    service,
    dataSetId,
    annotationSetId,
    schemaName,
    name,
    rawDataId,
    payloadRaw,
    resetForm,
    onCreated,
    onOpenChange,
    scenarioFactory,
  ]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) resetForm();
        onOpenChange(d.open);
      }}
    >
      <DialogSurface className={styles.dialogSurfaceWide}>
        <DialogBody>
          <DialogTitle>Add Annotation Data</DialogTitle>
          <DialogContent>
            <div className={styles.dialogForm}>
              {error !== null && (
                <Text className={styles.errorState}>{error}</Text>
              )}
              <Field label="Name" required>
                <Input
                  value={name}
                  onChange={(_, d) => setName(d.value)}
                  placeholder="Entry name"
                />
              </Field>
              <Field
                label="Raw Data ID"
                required
                hint="Select a data entry from this dataset, or paste an ID directly."
              >
                <Input
                  value={rawDataId}
                  onChange={(_, d) => setRawDataId(d.value)}
                  placeholder="Paste or pick below"
                />
              </Field>

              <RawDataPicker
                entries={dataEntries}
                selectedId={rawDataId}
                onSelect={setRawDataId}
                isLoading={isLoadingEntries}
              />

              <Field
                label="Category"
                hint="Derived from the annotation set's schema."
              >
                <Input value={schemaName} readOnly disabled />
              </Field>

              <Field
                label="Payload (JSON)"
                validationMessage={jsonError ?? undefined}
                validationState={jsonError !== null ? 'error' : 'none'}
              >
                <Textarea
                  value={payloadRaw}
                  onChange={(_, d) => {
                    setPayloadRaw(d.value);
                    setJsonError(null);
                  }}
                  rows={5}
                  className={styles.monoTextarea}
                  placeholder='{"key": "value"}'
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={() => {
                void handleSubmit();
              }}
              disabled={!name.trim() || !rawDataId.trim() || isSubmitting}
            >
              {isSubmitting ? 'Adding…' : 'Add'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
